import {
  getMatchSetTrackHeaders,
  getMatchSetRosterDisplayScore,
  getMatchSetDisplayScores,
  getMatchSetTracksRosterDisplayScores,
} from "./Set/MatchSet";
import { Match, matchCompleteByByesOrForfeits } from "./Match";
import { hasSetAdvantage } from "./MatchFormat";
import { getResults } from "model/Results";
import { getRoster, isForfeited } from "../Roster/Roster";

export const getRosterDisplayScore = (match: Match, rosterId: string) => {
  const roster = getRoster(match.teams.rosters, rosterId);
  if (!roster) {
    return undefined;
  }

  if (isForfeited(roster)) {
    return "F";
  }

  if (matchCompleteByByesOrForfeits(match.teams.rosters)) {
    return "W";
  }

  const scores =
    match.format.type === "Single"
      ? getMatchSetDisplayScores(match.sets[0])
      : match.scores;

  if (!scores) {
    return undefined;
  }

  return scores[rosterId]?.toString() ?? "-";
};

export type PartScore = {
  score: string;
  type: "Track" | "Set";
  winner: boolean | undefined;
};

export const getMatchPartScores = (
  match: Match,
  rosterId: string,
  includeMultiSetTrackScores: boolean
): PartScore[] => {
  if (
    matchCompleteByByesOrForfeits(match.teams.rosters) ||
    match.sets.length === 0
  ) {
    return [];
  }

  if (match.format.type === "Single") {
    const set = match.sets.at(0);
    if (!set || set.format.numTracks === 1) {
      return [];
    }

    return (
      getMatchSetTracksRosterDisplayScores(set, rosterId)?.map((s) => ({
        score: s,
        type: "Track",
        winner: undefined,
      })) ?? []
    );
  }

  const partScores: PartScore[] = [];
  if (hasSetAdvantage(match.format)) {
    const teamHasSetAdvantage =
      match.format.setAdvantageRosterIds?.includes(rosterId) ?? false;
    partScores.push({
      score: teamHasSetAdvantage ? "*" : "0",
      type: "Set",
      winner: teamHasSetAdvantage,
    });
  }

  for (let i = 0; i < match.sets.length; i++) {
    const set = match.sets[i];
    const setDisplayScore = getMatchSetRosterDisplayScore(set, rosterId);
    const setResults = getResults(set.finalPositions, set.format.numWinners);
    partScores.push({
      score: setDisplayScore,
      type: "Set",
      winner: !!setResults ? setResults[rosterId] === "W" : undefined,
    });

    if (
      set.type !== "TimeAttack" &&
      (!set.format.numTracks || set.format.numTracks > 1) &&
      includeMultiSetTrackScores
    ) {
      getMatchSetTracksRosterDisplayScores(set, rosterId)?.forEach((score) =>
        partScores.push({
          score,
          type: "Track",
          winner: undefined,
        })
      );
    }
  }

  return partScores;
};

export type PartHeader = {
  value: string;
  type: "Track" | "Set";
};

// todo - combine these getMatchPART... methods, can return headers and all teams values in one method?
export const getMatchPartHeaders = (match: Match): PartHeader[] => {
  if (matchCompleteByByesOrForfeits(match.teams.rosters)) {
    return [];
  }

  if (match.format.type === "Single") {
    const set = match.sets.at(0);
    if (!set || set.format.numTracks === 1) {
      return [];
    }

    return (
      getMatchSetTrackHeaders(set)?.map((h) => ({ value: h, type: "Track" })) ??
      []
    );
  }

  const partHeaders: PartHeader[] = [];
  var setCount = 1;
  if (hasSetAdvantage(match.format)) {
    partHeaders.push({ value: "1", type: "Set" });
    setCount++;
  }

  for (let i = 0; i < match.sets.length; i++) {
    const set = match.sets[i];
    partHeaders.push({ value: setCount.toString(), type: "Set" });
    setCount++;
    if (set.format.numTracks === 1) {
      continue;
    }

    getMatchSetTrackHeaders(set)?.forEach((h) =>
      partHeaders.push({ value: h, type: "Track" })
    );
  }

  return partHeaders;
};
